import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useState, type ReactNode } from "react";

export type ConfirmRequest = {
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  busyLabel?: string;
  /** Runs on confirm; the dialog stays open (showing the error) if it throws. */
  onConfirm: () => Promise<void> | void;
};

/**
 * In-app replacement for window.confirm for destructive clipboard actions.
 * Keeps the app's own styling, runs the action inline with a busy state, and
 * surfaces failures in place instead of a native dialog + silent catch.
 */
export const ConfirmDialog = ({
  request,
  onClose,
}: {
  request: ConfirmRequest | null;
  onClose: () => void;
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!request) return;
    setBusy(true);
    setError(null);
    try {
      await request.onConfirm();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open={!!request} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{request?.title}</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-zinc-500">{request?.description}</div>
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p className="mt-2 rounded-[3px] bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} loading={busy}>
            {busy ? (request?.busyLabel ?? "Working…") : (request?.confirmLabel ?? "Delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
